import React from 'react';
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import AuthorsSidebar from '../../components/AuthorsSidebar';
import {
  ExpandMore as ExpandMoreIcon,
  Description,
  Edit,
  CheckCircle,
  Info,
} from '@mui/icons-material';

const PublicationRules: React.FC = () => {
  const editorialRules = [
    'Статьи должны соответствовать тематике журнала',
    'Материалы должны быть оригинальными и не публиковаться ранее',
    'Все авторы должны согласиться с публикацией',
    'Статья должна содержать все необходимые разделы',
    'Аннотация (на русском и английском): от 150 до 200 слов',
    'Ключевые слова (на русском и английском): 7–10 ключевых слов или словосочетаний',
    'Список литературы: не менее 10 источников, самоцитирование не более 20%',
  ];

  const manuscriptPreparation = [
    {
      title: 'Набор текста',
      items: [
        'Формат: Microsoft Word',
        'Шрифт: Times New Roman',
        'Размер шрифта: 14',
        'Межстрочный интервал: 1,0',
        'Выравнивание текста: по ширине',
        'Поля: левое — 2 см, правое, верхнее, нижнее — по 2 см',
        'Отступ первой строки (абзацный отступ): 1,25 см (для образования отступа не следует использовать клавишу пробела и табуляции)',
      ],
    },
    {
      title: 'Оформление аннотации, ключевых слов, примечаний, библиографии, сведений об авторе',
      items: [
        'Шрифт: 12',
        'Интервал: 1,0',
        'Аннотация (на русском и английском): от 150 до 200 слов',
        'Аннотация должна иметь информативный характер и отражать актуальность, цели научного исследования, используемую методологию, основные научные результаты, полученные автором (-ами) и их практическое значение',
        'Ключевые слова (на русском и английском языках): 7–10 ключевых слов или словосочетаний',
        'Ключевые слова должны отражать основное содержание статьи, определять предметную область исследования, встречаться в тексте статьи',
      ],
    },
    {
      title: 'Объем статьи',
      items: [
        'Объем статьи: от 8 до 15 машинописных страниц',
        'Не менее 10000 и не более 30000 знаков (с учетом пробелов)',
        'Список литературы в расчете объема статьи не учитывается',
        'В расчет объема статьи входят аннотация, ключевые слова, библиография, сведения об авторе(ах)',
      ],
    },
    {
      title: 'Таблицы',
      items: [
        'Таблицы должны содержать экспериментальные данные и представлять собой обобщенные и статистические обработанные материалы исследований',
        'Каждая таблица должна иметь заголовок',
        'Таблицы должны быть обязательно пронумерованы и упомянуты в тексте',
      ],
    },
    {
      title: 'Иллюстрации',
      items: [
        'Данные рисунков не должны повторять материалы таблиц',
        'Рисунки должны быть четкими, легко воспроизводимыми',
        'Рисунки должны быть обязательно пронумерованы, упомянуты в тексте и иметь подрисуночные подписи',
        'Объяснение значений всех условных обозначений',
        'При заимствовании рисунков необходимо указывать ссылку на источник в формате [1]',
        'Кегль текста в схемах: 9 пт',
      ],
    },
    {
      title: 'Сведения об авторах',
      items: [
        'ФИО, должности, ученые степени, ученые звания, место работы и адрес электронной почты (на русском и английском языках)',
        'SPIN-код: 0000-0000, AuthorID: 000000 (при наличии)',
        'Для аспирантов, соискателей ученой степени кандидата наук в обязательном порядке необходимо указывать:',
        '  - телефоны кафедр, научных подразделений, к которым они прикреплены',
        '  - данные о научном руководителе (фамилия, имя, отчество, ученая степень, звание)',
      ],
    },
  ];

  const informationFormatting = [
    'Единообразное оформление ссылок',
    'Правильное оформление таблиц',
    'Корректное использование аббревиатур',
    'Соблюдение стандартов цитирования',
  ];

  return (
    <AuthorsSidebar>
      <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Правила публикации
      </Typography>
      
      <Typography variant="body1" paragraph>
        Данный раздел содержит подробные правила и требования для авторов, 
        которые необходимо соблюдать при подготовке и подаче статей.
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description />
          Редакционные правила для авторов
        </Typography>
        <List>
          {editorialRules.map((rule, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText primary={rule} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit />
          Правила подготовки рукописей
        </Typography>
        
        {manuscriptPreparation.map((section, sectionIndex) => (
          <Accordion key={sectionIndex} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">{section.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {section.items.map((item, itemIndex) => (
                  <ListItem key={itemIndex}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Оформление информации
        </Typography>
        <Typography variant="body1" paragraph>
          Правильное оформление информации способствует лучшему пониманию и восприятию статьи.
        </Typography>
        <List>
          {informationFormatting.map((item, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Info color="primary" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Последовательность оформления статьи
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          В начале статьи (на первой странице) приводятся на русском языке:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="1. Сведения об авторах с указанием Ф.И.О., ученой степени (при наличии), места работы, должности, звания, электронного адреса; SPIN-код: 0000-0000, AuthorID: 000000 (при наличии)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="2. Название статьи (буквы – прописные)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="3. Индекс универсальной десятичной классификации (УДК)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="4. Аннотация и ключевые слова на русском языке (выравнивание «По ширине», без отступа)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="5. Указание на источник финансирования (если работа выполняется по гранту)" />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Затем размещается информация на английском языке:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="1. Сведения об авторах с указанием Ф.И.О., ученой степени (при наличии), места работы, должности, звания" />
          </ListItem>
          <ListItem>
            <ListItemText primary="2. Название статьи (буквы – прописные)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="3. Аннотация и ключевые слова (выравнивание «По ширине», без отступа)" />
          </ListItem>
        </List>

        <Typography variant="body2" paragraph sx={{ mt: 2 }}>
          После размещения технической информации следует текст статьи.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Структура статьи, как правило, включает:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Введение (характеристика актуальности темы и степени ее научной разработанности), указание на цель и задачи данной статьи" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Методы: обозначение и изложение использованных методов, математических моделей, программных комплексов" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Результаты: представление результатов экспериментов, графическая интерпретация, полученные аналитические зависимости и т.д." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Обсуждение результатов" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Выводы, заключение" />
          </ListItem>
        </List>

        <Typography variant="body2" paragraph sx={{ mt: 2 }}>
          Отдельные блоки содержания выделяются подзаголовками (оформляются с выравниванием «По центру», строчными, полужирным курсивом).
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Оформление ссылок и библиографии:
        </Typography>
        <Typography variant="body2" paragraph>
          Ссылка на источник из списка литературы должна быть указана в квадратных скобках, например, [1]. 
          Может быть указана ссылка на источник со страницей, например, [1, c. 57], на группу источников, например, [1; 2]. 
          Постраничные сноски допускаются только, если не содержат ссылку на библиографический источник, 
          например разъяснение термина.
        </Typography>
        <Typography variant="body2" paragraph>
          При оформлении списка литературы необходимо руководствоваться правилами ГОСТ 7.05-2008.
        </Typography>
        <Typography variant="body2" paragraph>
          Список литературы должен состоять не менее чем из 10 источников. Самоцитирование не более 20%.
        </Typography>
        <Typography variant="body2" paragraph>
          При обзорном характере статьи количество источников рекомендуется не менее 20 источников.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Примерный образец оформления
        </Typography>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" component="pre" sx={{ 
            whiteSpace: 'pre-wrap', 
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.6
          }}>
{`Иванова Ирина Ивановна — кандидат юридических наук, доцент, профессор кафедры государственного и муниципального управления Института социально-гуманитарного образования Московского педагогического государственного университета, е-mail: 1234567@mail.ru

SPIN-код: 0000-0000, AuthorID: 000000 (при наличии)

СОВРЕМЕННЫЕ КРИТЕРИИ ОЦЕНКИ НАУЧНОЙ ДЕЯТЕЛЬНОСТИ ВУЗОВ: РОССИЙСКИЙ И ЗАРУБЕЖНЫЙ ОПЫТ

Аннотация. Актуальность исследования вызвана … Цель: … Методология исследования — … В результате обосновано … Во-первых, … Во-вторых, … Авторы приходят к выводу, что … В этой связи важно отметить …

Ключевые слова: критерии оценки, высшая школа, научно-исследовательская работа, индекс Хирша, образовательное право.

Ivanova Irina Ivanovna — PhD in Law, Associate Professor, Professor of the Department of State and Municipal Administration of the Institute of Social and Humanitarian Education of the Moscow Pedagogical State University, e-mail: 1234567@mail.ru

MODERN CRITERIA FOR THE ASSESSMENT OF SCIENTIFIC ACTIVITY OF HIGHER EDUCATION INSTITUTIONS: RUSSIAN AND FOREIGN EXPERIENCE

Annotation. The relevance of the research is caused by … Purpose: … The research methodology is … As a result, it is justified … First, … Second, … The authors conclude that … In this connection, it is important to note …

Key words: assessment criteria, higher education, research work, Hirsch index, educational law.

Текст статьи ………………………………………………………………………………………………………………………………

Список литературы:

1. Адизес И.К. Об образовании [Электронный ресурс] / Сайт «Институт Адизеса в России». – Режим доступа: http://adizes.ru/ichak-adizes-blog/ob-obrazovanii/ (дата обращения: 10.08.2025). 2. Боброва Н.А. Первый профессорский форум // Проблемы науки . 2018. № 3 (37). – С. 66 – 68. 3. Галажинский Э. Зачем нужны рейтинги университетов [Электронный ресурс] / Сайт «Ведомости». – Режим доступа: https://www.vedomosti.ru/opinion/articles/2017/07/17/723970-reitingi-universitetov (дата обращения: 20.08.2018).

4. Кузьмина Н.Б. Эффективный университет: перезагрузка. М.: МГИМО-Университет, 2014. – 134 с. 5. Постановление Правительства РФ от 15 апреля 2014 г. № 295 «Об утверждении государственной программы Российской Федерации «Развитие образования» на 2013-2020 годы» (с изменениями и дополнениями) [Электронный ресурс] / Информационно-правовой портал «Гарант» (дата обращения: 20.08.2018). 6. Сапожков О. Для экономики знаний России придется переучиваться [Электронный ресурс] / Сайт «Коммерсантъ». – Режим доступа: https://www.kommersant.ru/doc/3450155?from=four_economic (дата обращения: 15.08.2018). 7. Wuestewald T. Adult Learning in Executive Development Programs [Электронный ресурс] // Adult Learning. 2016. Vol. 27. N 2. P. 68-75. Режим доступа: https://doi.org/10.1177/1045159515602256 (дата обращения 30.07.2018).

Spisok literatury:

1.Adizes I.K. Ob obrazovanii [Elektronnyy resurs] / Sayt «Institut Adizesa v Rossii». – Rezhim dostupa: http://adizes.ru/ichak-adizes-blog/ob-obrazovanii/ (data obrashcheniya: 10.08.2018).

2. Bobrova N.A. Pervyy professorskiy forum // Problemy nauki . 2018. № 3 (37). – S. 66 – 68.

3. Galazhinskiy E. Zachem nuzhny reytingi universitetov [Elektronnyy resurs] / Sayt «Vedomosti». – Rezhim dostupa: https://www.vedomosti.ru/opinion/articles/2017/07/17/723970-reitingi-universitetov (data obrashcheniya: 20.08.2018).

4. Kuz'mina N.B. Effektivnyy universitet: perezagruzka. M.: MGIMO-Universitet, 2014. – 134 s.

 5. Postanovleniye Pravitel'stva RF ot 15 aprelya 2014 g. № 295 «Ob utverzhdenii gosudarstvennoy programmy Rossiyskoy Federatsii «Razvitiye obrazovaniya» na 2013-2020 gody» (s izmeneniyami i dopolneniyami) [Elektronnyy resurs] / Informatsionno-pravovoy portal «Garant» (data obrashcheniya: 20.08.2018).

6. Sapozhkov O. Dlya ekonomiki znaniy Rossii pridetsya pereuchivat'sya [Elektronnyy resurs] / Sayt «Kommersant»». – Rezhim dostupa: https://www.kommersant.ru/doc/3450155?from=four_economic (data obrashcheniya: 15.08.2018). 7. Wuestewald T. Adult Learning in Executive Development Programs [Elektronnyy resurs] // Adult Learning. 2016. Vol. 27. N 2. P. 68-75. Rezhim dostupa : https://doi.org/10.1177/1045159515602256 (data obrashcheniya 30.07.2018).`}
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" paragraph>
            <strong>Правила транслитерации:</strong>
          </Typography>
          <Typography variant="body2" component="div" sx={{ pl: 2 }}>
            <p>Транслитерацию рекомендуется делать с применением инструментов сайта www.translit.ru, выбирать вариант LC (Library of Congress).</p>
            <p>Транслитерированные русские источники следует оформлять по модели для российских источников. В квадратных скобках давать перевод названия статьи или книги, а также периодического издания, в котором статья была опубликована.</p>
            <p>На английском языке фамилия отделяется от инициалов автора через запятую, затем год издания работы в скобках (только цифры).</p>
            <p>Описание статьи, опубликованной на русском языке, делается по следующей схеме:</p>
            <ul style={{ marginLeft: '20px', paddingLeft: '20px' }}>
              <li>фамилия и инициалы автора (авторов) — в транслитерации по системе LC (http://translit.ru/?direction=ru&account=lc);</li>
              <li>заглавие статьи / монографии — в транслитерации;</li>
              <li>заглавие статьи / монографии — в переводе на английский язык, в квадратных скобках;</li>
              <li>название журнала в транслитерации которое отделено от заглавия статьи двойным слешем (//);</li>
              <li>название журнала — в переводе на английский язык, в квадратных скобках (название на английском языке смотреть на сайте журнала);</li>
              <li>выходные данные, исключая год (город, название издательства, «дата обращения») — в транслитерации, заменив обозначение страниц статьи (общее количество страниц монографий, учебников, сборников материалов конференций) на латинское P. (p.);</li>
              <li>указание на язык публикации, вышедшей не на английском языке [напр.: (In Russ.)], — обязательно!</li>
            </ul>
          </Typography>
        </Alert>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Электронная корректура
        </Typography>
        <Typography variant="body1" paragraph>
          После принятия статьи к публикации авторы получают электронную корректуру для проверки 
          и внесения необходимых исправлений.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Процесс корректуры:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="1. Получение электронной корректуры от редакции" />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. Проверка текста на наличие ошибок" />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Внесение исправлений (только критические ошибки)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="4. Возврат корректуры в установленные сроки" />
            </ListItem>
          </List>
        </Box>
      </Paper>
      </Container>
    </AuthorsSidebar>
  );
};

export default PublicationRules;
